import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ConfigurationApiClient from '../../api/configurationApiClient';

/**
 * Whether the console shows desktop (Windows / macOS) devices and licences.
 *
 * Driven by the console's own configuration store, key `devices.show-desktop`, so a
 * deployment can switch it without a code change:
 *
 *   PUT /api/configurations/devices.show-desktop
 *   {"configValue": "true", "category": "ui", "description": "Show desktop devices and licences"}
 *
 * A missing key, any value other than true/1/yes/on, or a failed read all mean HIDDEN —
 * the default for a fresh environment. The flag is read once after login; `refresh` re-reads it.
 */
export const SHOW_DESKTOP_CONFIG_KEY = "devices.show-desktop";

const TRUE_VALUES = new Set(["true", "1", "yes", "on"]);

const DeviceVisibilityContext = createContext(null);

export const useDeviceVisibility = () => {
    const context = useContext(DeviceVisibilityContext);
    if (!context) {
        throw new Error('useDeviceVisibility must be used within DeviceVisibilityProvider');
    }
    return context;
};

export const DeviceVisibilityProvider = ({ children }) => {
    const [showDesktop, setShowDesktop] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const response = await ConfigurationApiClient.fetchConfigurationByKey(SHOW_DESKTOP_CONFIG_KEY);
            const raw = String(response?.data?.configValue ?? "").trim().toLowerCase();
            setShowDesktop(TRUE_VALUES.has(raw));
        } catch (error) {
            // Not configured (404) or unreadable: desktop stays hidden.
            setShowDesktop(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const value = useMemo(() => ({ showDesktop, loading, refresh: load }), [showDesktop, loading, load]);

    return (
        <DeviceVisibilityContext.Provider value={value}>
            {children}
        </DeviceVisibilityContext.Provider>
    );
};
